import { useEffect, useRef, useState } from "react";
import { CalendarPlus, Moon, Plus, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/cn.js";

/**
 * Mobile-friendly floating action button for the calendar. Tap → action sheet
 * with the three primary create flows. Always visible; sits above the bottom
 * tab bar on mobile and at the bottom-right of the viewport on desktop.
 *
 * Closes on outside-click, Escape, and after any action fires. Honors
 * `prefers-reduced-motion` (NFR-CUX-004) by limiting transitions to opacity.
 */
export function CalendarFab({
  onNewAppointment,
  onWalkin,
  onBlockTime
}: {
  onNewAppointment: () => void;
  onWalkin: () => void;
  onBlockTime: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(action: () => void): void {
    setOpen(false);
    action();
  }

  return (
    <div
      ref={containerRef}
      data-testid="calendar-fab"
      // Sits above the bottom-tab bar (h-14) on mobile; standard inset on desktop.
      className="fixed bottom-20 right-4 z-30 flex flex-col items-end gap-2 lg:bottom-6 lg:right-6"
    >
      {open && (
        <div
          role="menu"
          aria-label="Create"
          className="flex flex-col items-stretch gap-1.5 rounded-xl border bg-card p-1.5 shadow-lg animate-fade-in motion-reduce:animate-none"
        >
          <FabAction
            label="New appointment"
            icon={CalendarPlus}
            onClick={() => pick(onNewAppointment)}
          />
          <FabAction
            label="Walk-in"
            icon={UserPlus}
            onClick={() => pick(onWalkin)}
          />
          <FabAction
            label="Block time"
            icon={Moon}
            onClick={() => pick(onBlockTime)}
          />
        </div>
      )}
      <button
        type="button"
        aria-label={open ? "Close create menu" : "Create"}
        aria-expanded={open}
        data-testid="calendar-fab-button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform duration-150 hover:shadow-xl active:scale-95 motion-reduce:transition-none",
          open && "rotate-45"
        )}
      >
        {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
      </button>
    </div>
  );
}

function FabAction({
  label,
  icon: Icon,
  onClick
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors duration-100 hover:bg-accent motion-reduce:transition-none"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span>{label}</span>
    </button>
  );
}
